import moment from 'moment';
import 'moment/locale/sv';
import { registerLocale } from 'react-datepicker';
import svSE from 'date-fns/locale/sv';
import enGB from 'date-fns/locale/en-GB';
import { DEFAULT_LANGUAGE } from './languages';

/**
 * Single owner of every locale-dependent date concern: the global moment locale, the date-fns
 * locales react-datepicker is given, and the short month/weekday labels rendered in the schedule.
 * Views must not import a locale directly.
 */

/**
 * BCP 47 tags passed to Date#toLocaleString. `sv-se` is what the app used before it was
 * multilingual, so Swedish output stays byte-identical.
 */
const INTL_TAGS = {
    sv: 'sv-se',
    en: 'en-gb'
};

/**
 * moment locale ids. moment ships `en` built in; the formats this app uses ('dddd D MMMM, HH:mm',
 * 'ddd D MMM') differ between `en` and `en-gb` only in tokens we never emit, so `en` costs no bytes
 * and changes nothing. `sv` is registered by the side-effect import above.
 */
const MOMENT_LOCALES = {
    sv: 'sv',
    en: 'en'
};

const DATE_FNS_LOCALES = {
    sv: svSE,
    en: enGB
};

const registeredScheduleLocales = new Set();

function resolve(language) {
    const code = typeof language === 'string' ? language.split(/[-_]/)[0].toLowerCase() : null;

    return DATE_FNS_LOCALES[code] != null ? code : DEFAULT_LANGUAGE;
}

/**
 * Sets moment's global locale. This is the only place that does so.
 *
 * Note this is safe with respect to week handling: nothing in the app uses moment's week-sensitive
 * APIs (startOf('week'), .week(), .weekday(), isoWeek). Week start and week numbers come from
 * Tools.getWeekStart / Tools.getWeekNumberForDate, which take organisation.first_weekday explicitly.
 * @param {string} language ISO 639-1 code
 */
export function applyDateLocale(language) {
    moment.locale(MOMENT_LOCALES[resolve(language)]);
}

/**
 * Registers — and returns the key of — a react-datepicker locale whose week start is the
 * organisation's, not the language's.
 *
 * The override is deliberate and must survive every language: en-US starts weeks on Sunday, so
 * letting the locale decide would silently shift a Monday organisation's schedule when an admin
 * switches to English.
 * @param {string} language ISO 639-1 code
 * @param {number} firstWeekday 1=Monday … 7=Sunday, same encoding as Tools.getWeekStart
 * @returns {string} Locale key to pass to react-datepicker's locale prop
 */
export function ensureScheduleLocale(language, firstWeekday) {
    const fw =
        Number.isFinite(Number(firstWeekday)) && firstWeekday >= 1 && firstWeekday <= 7
            ? Number(firstWeekday)
            : 1;
    const code = resolve(language);
    const key = `${code}-fw-${fw}`;

    if (!registeredScheduleLocales.has(key)) {
        registeredScheduleLocales.add(key);

        const base = DATE_FNS_LOCALES[code];
        const weekStartsOn = fw === 7 ? 0 : fw;

        registerLocale(key, {
            ...base,
            options: {
                ...base.options,
                weekStartsOn
            }
        });
    }

    return key;
}

/**
 * @param {Date} date
 * @param {string} language ISO 639-1 code
 * @returns {string} Abbreviated month, e.g. "aug." / "Aug"
 */
export function formatMonthShort(date, language) {
    return date.toLocaleString(INTL_TAGS[resolve(language)], { month: 'short' });
}

/**
 * @param {Date} date
 * @param {string} language ISO 639-1 code
 * @returns {string} Abbreviated weekday, e.g. "mån" / "Mon"
 */
export function formatWeekdayShort(date, language) {
    return date.toLocaleString(INTL_TAGS[resolve(language)], { weekday: 'short' });
}

/**
 * Weekday names in the active language, Monday first. These are locale data, not UI copy, so they
 * come from the date library rather than from a translation file — a new language needs no new keys.
 *
 * They come from date-fns rather than Date#toLocaleString because the pinned Node 12 toolchain is
 * built with small-icu and only knows English, which would make this untestable.
 *
 * Names are returned exactly as the locale spells them, which is lower case in Swedish. Callers
 * that want a leading capital do it in CSS, because casing rules are language-specific and doing it
 * in JavaScript is wrong in several languages.
 * @param {string} language ISO 639-1 code
 * @returns {Array<{value: string, label: string}>} value is 1..7 (1=Monday … 7=Sunday)
 */
export function getWeekdayNames(language) {
    const locale = DATE_FNS_LOCALES[resolve(language)];
    const names = [];

    // date-fns indexes days from Sunday, this list runs Monday..Sunday.
    for (let weekday = 1; weekday <= 7; weekday++) {
        names.push({
            value: String(weekday),
            label: locale.localize.day(weekday === 7 ? 0 : weekday, { width: 'wide' })
        });
    }

    return names;
}
